import { useState } from 'react';
import { useUser } from '@/hooks/useUser';
import { PageWrapper } from '@/layout/components/PageWrapper';
import { PageHeader } from '@/layout/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { ConfirmPopover } from '@/components/ui/ConfirmPopover';
import { Megaphone, Plus, Pencil, Sparkles, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useGetAnnouncementsQuery, useDeleteAnnouncementMutation } from '@/store/announcementsApi';
import { Announcement, AnnouncementType } from '@/types/Announcement';
import { AnnouncementFormModal } from './components/AnnouncementFormModal';
import { AnnouncementAiGenerateModal } from './components/AnnouncementAiGenerateModal';

const TYPE_LABELS: Record<AnnouncementType, string> = {
    NEWS: 'מה חדש',
    MSG: 'הודעה',
    LINK: 'קישור',
};

const TYPE_BADGE_VARIANT: Record<AnnouncementType, 'default' | 'secondary' | 'outline'> = {
    NEWS: 'default',
    MSG: 'secondary',
    LINK: 'outline',
};

export default function AnnouncementsAdminPage() {
    const { user } = useUser();
    const { data: announcements = [], isLoading } = useGetAnnouncementsQuery(undefined, { skip: !user?.is_superuser });
    const [deleteAnnouncement] = useDeleteAnnouncementMutation();
    const [formOpen, setFormOpen] = useState(false);
    const [editing, setEditing] = useState<Announcement | null>(null);
    const [aiOpen, setAiOpen] = useState(false);
    const [aiDraft, setAiDraft] = useState<Pick<Announcement, 'type' | 'desc' | 'long'> | null>(null);

    const sortedAnnouncements = [...announcements].sort((a, b) => b.order - a.order);
    const nextOrder = announcements.length ? Math.max(...announcements.map((a) => a.order)) + 1 : 1;

    if (!user?.is_superuser) {
        return (
            <PageWrapper>
                <div className="text-center py-20 text-gray-500">
                    <p className="text-xl">גישה מוגבלת לסופר יוזר בלבד</p>
                </div>
            </PageWrapper>
        );
    }

    function openCreate() {
        setEditing(null);
        setAiDraft(null);
        setFormOpen(true);
    }

    function openEdit(item: Announcement) {
        setEditing(item);
        setAiDraft(null);
        setFormOpen(true);
    }

    function handleAiGenerated(draft: Pick<Announcement, 'type' | 'desc' | 'long'>) {
        setEditing(null);
        setAiDraft(draft);
        setFormOpen(true);
    }

    async function handleDelete(id: number) {
        try {
            await deleteAnnouncement(id).unwrap();
            toast.success('ההודעה נמחקה');
        } catch {
            toast.error('שגיאה במחיקת ההודעה');
        }
    }

    return (
        <PageWrapper>
            <PageHeader title="ניהול הודעות ועדכונים" />

            <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-gray-500">{announcements.length} הודעות</p>
                <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setAiOpen(true)}>
                        <Sparkles className="h-4 w-4 ml-1 text-amber-500" />
                        עדכון באמצעות AI
                    </Button>
                    <Button size="sm" onClick={openCreate}>
                        <Plus className="h-4 w-4 ml-1" />
                        הודעה חדשה
                    </Button>
                </div>
            </div>

            {isLoading ? (
                <div className="flex justify-center py-12"><Spinner /></div>
            ) : announcements.length === 0 ? (
                <div className="text-center py-16 text-gray-400 border border-dashed rounded-lg">
                    <Megaphone className="h-10 w-10 mx-auto mb-3 opacity-30" />
                    <p>אין הודעות עדיין. לחץ "הודעה חדשה" להתחיל.</p>
                </div>
            ) : (
                <div className="grid gap-3">
                    {sortedAnnouncements.map((item) => (
                        <div key={item.id} className="flex items-start justify-between gap-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
                            <div className="min-w-0 flex-1 space-y-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <Badge variant="outline" className="text-gray-500" title="סדר תצוגה">
                                        סדר: {item.order}
                                    </Badge>
                                    <Badge variant={TYPE_BADGE_VARIANT[item.type]}>{TYPE_LABELS[item.type]}</Badge>
                                    {!item.is_active && <Badge variant="outline" className="text-gray-400">לא פעיל</Badge>}
                                    <span className="font-medium">{item.desc || '(ללא כותרת)'}</span>
                                </div>
                                <p className="text-xs text-gray-500">
                                    נוצר: {new Date(item.created_at).toLocaleString('he-IL')}
                                    {item.valid_until && ` · בתוקף עד: ${new Date(item.valid_until).toLocaleString('he-IL')}`}
                                </p>
                            </div>
                            <div className="flex items-center gap-1 shrink-0">
                                <Button variant="ghost" size="icon-sm" onClick={() => openEdit(item)}>
                                    <Pencil className="h-4 w-4" />
                                </Button>
                                <ConfirmPopover onConfirm={() => handleDelete(item.id)} message="למחוק את ההודעה?">
                                    <Button variant="ghost" size="icon-sm">
                                        <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                </ConfirmPopover>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <AnnouncementFormModal open={formOpen} onOpenChange={setFormOpen} initialData={editing} initialDraft={aiDraft} defaultOrder={nextOrder} />
            <AnnouncementAiGenerateModal open={aiOpen} onOpenChange={setAiOpen} onGenerated={handleAiGenerated} />
        </PageWrapper>
    );
}
